import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import { useCallback } from 'react';
import {
  createUltimatePagination,
  ITEM_TYPES
} from 'react-ultimate-pagination';

import { PAGE_SIZE } from '../constants';
import clsx from 'clsx';

// Custom hook to create a href without loosing any query params of the page
// Takes queryparam and it's value as an argument
function useQueryParam(name, value) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const params = new URLSearchParams(searchParams);
  params.set(name, value);
  const href = pathname + '?' + params.toString();
  return [href];
}

// Using react-ultimate-pagination
// https://github.com/ultimate-pagination/react-ultimate-pagination

const WrapperComponent = ({ children }) => (
  <ul className="pagination">{children}</ul>
);

const withPreventDefault = (fn) => (event) => {
  event.preventDefault();
  fn();
};

const PageLink = ({ onClick, isActive, value }) => {
  const [href] = useQueryParam('page', value);
  return (
    <li className="page-item">
      <a
        className={clsx('page-link', isActive && 'disabled')}
        href={href}
        onClick={withPreventDefault(onClick)}>
        {value}
      </a>
    </li>
  );
};

const Ellipsis = ({ onClick, isActive, value }) => {
  const [href] = useQueryParam('page', value);
  return (
    <li className="page-item">
      <a
        className={clsx('page-link', isActive && 'disabled')}
        href={href}
        onClick={withPreventDefault(onClick)}>
        ...
      </a>
    </li>
  );
};

const FirstPageLink = ({ onClick, isActive, value }) => {
  const [href] = useQueryParam('page', value);
  return (
    <li className="page-item">
      <a
        className={clsx('page-link', isActive && 'disabled')}
        href={href}
        onClick={withPreventDefault(onClick)}>
        &lsaquo; First
      </a>
    </li>
  );
};

const LastPageLink = ({ onClick, isActive, value }) => {
  const [href] = useQueryParam('page', value);
  return (
    <li className="page-item">
      <a
        className={clsx('page-link', isActive && 'disabled')}
        href={href}
        onClick={withPreventDefault(onClick)}>
        Last &rsaquo;
      </a>
    </li>
  );
};

const itemTypeToComponent = {
  [ITEM_TYPES.PAGE]: PageLink,
  [ITEM_TYPES.ELLIPSIS]: Ellipsis,
  [ITEM_TYPES.FIRST_PAGE_LINK]: FirstPageLink,
  [ITEM_TYPES.PREVIOUS_PAGE_LINK]: () => null,
  [ITEM_TYPES.NEXT_PAGE_LINK]: () => null,
  [ITEM_TYPES.LAST_PAGE_LINK]: LastPageLink
};

const UltimatePagination = createUltimatePagination({
  itemTypeToComponent,
  WrapperComponent
});

function Paginate({ current, total, perPage }) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Get a new searchParams string by merging the current
  // searchParams with a provided key/value pair
  const createQueryString = useCallback(
    (name, value) => {
      const params = new URLSearchParams(searchParams);
      params.set(name, value);

      return params.toString();
    },
    [searchParams]
  );

  if (!total) return null;
  const totalPages = Math.ceil(total / perPage) || 1;
  if (totalPages <= 1) return null;
  const currentPage = parseInt(current);
  if (currentPage > totalPages) return null;
  const onChange = (page) =>
    router.push(pathname + '?' + createQueryString('page', page));

  return (
    <div className="text-center my-5">
      <UltimatePagination
        currentPage={currentPage}
        totalPages={totalPages}
        onChange={onChange}
      />
    </div>
  );
}

Paginate.defaultProps = {
  perPage: PAGE_SIZE
};

export default Paginate;
